(() => {
  // ── IndexedDB helpers ───────────────────────────────────────────────────────
  // Canvas PNG data URLs are 2–5 MB; localStorage's 5 MB limit causes a silent
  // QuotaExceededError. IndexedDB has no practical size limit.
  const _PCD_DB   = 'pcd-designs';
  const _PCD_STORE = 'designs';

  function _pcdOpenDB() {
    return new Promise((res, rej) => {
      const r = indexedDB.open(_PCD_DB, 1);
      r.onupgradeneeded = e => e.target.result.createObjectStore(_PCD_STORE);
      r.onsuccess = e => res(e.target.result);
      r.onerror   = e => rej(e.target.error);
    });
  }

  function pcdIdbSet(key, value) {
    return _pcdOpenDB().then(db => new Promise((res, rej) => {
      const tx = db.transaction(_PCD_STORE, 'readwrite');
      tx.objectStore(_PCD_STORE).put(value, key);
      tx.oncomplete = res;
      tx.onerror    = e => rej(e.target.error);
    }));
  }
  // ── End IDB helpers ─────────────────────────────────────────────────────────

  const CUSTOMIZER_SELECTOR = '.product-customizer';
  const scriptCache = window.__productCustomizerScripts || (window.__productCustomizerScripts = {});
  const moduleCache = window.__productCustomizerModules || (window.__productCustomizerModules = {});
  const DEFAULT_IMGLY_VERSION = '1.67.0';
  const DEFAULT_IMGLY_SCRIPT_URL = `https://cdn.img.ly/packages/imgly/cesdk-js/${DEFAULT_IMGLY_VERSION}/index.js`;
  const DEFAULT_IMGLY_LICENSE =
    '9fCD0PZSWodWiGp-ZEFKXExpWxjzhjT4Ip8UV2Fa6po6Fr-gbeJ0ori_KHO2Dptd';

  const parseJson = (value, label) => {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch (error) {
      try {
        const trimmed = value.trim();
        const start = trimmed.indexOf('{');
        const end = trimmed.lastIndexOf('}');
        if (start !== -1 && end !== -1 && end > start) {
          const candidate = trimmed.slice(start, end + 1);
          const normalized = candidate
            .replace(/(\w+)\s*:/g, '"$1":')
            .replace(/'/g, '"')
            .replace(/,\s*([}\]])/g, '$1');
          return JSON.parse(normalized);
        }
      } catch (fallbackError) {
        console.warn('[ProductCustomizer] JSON recovery failed.', fallbackError);
      }
      console.warn(
        `[ProductCustomizer] Invalid JSON${label ? ` (${label})` : ''}.`,
        error
      );
      return null;
    }
  };

  const isModuleUrl = (url) =>
    Boolean(
      url &&
      ((url.includes('/cesdk-js/') && url.endsWith('/index.js') && !url.includes('/umd/')) ||
        url.includes('/esm/') ||
        url.endsWith('.mjs'))
    );

  const loadScriptOnce = (url, options = {}) => {
    if (!url) return Promise.resolve();
    if (scriptCache[url]) return scriptCache[url];

    scriptCache[url] = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      if (options.type) {
        script.type = options.type;
      }
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load ${url}`));
      document.head.appendChild(script);
    });

    return scriptCache[url];
  };

  const loadModuleOnce = (url) => {
    if (!url) return Promise.resolve(null);
    if (moduleCache[url]) return moduleCache[url];

    moduleCache[url] = import(url)
      .then((module) => {
        const sdk = module?.default || module;
        if (sdk) {
          window.CreativeEditorSDK = window.CreativeEditorSDK || sdk;
          window.CESDK = window.CESDK || sdk;
        }
        return sdk;
      })
      .catch((error) => {
        throw new Error(`Failed to import ${url}: ${error?.message || error}`);
      });

    return moduleCache[url];
  };

  const toModuleUrl = (url) => {
    if (!url) return url;
    if (url.includes('/umd/index.js')) {
      return url.replace('/umd/index.js', '/index.js');
    }
    return url;
  };

  const loadImglySdk = async (url, options = {}) => {
    if (!url) return;
    if (isModuleUrl(url)) {
      await loadModuleOnce(url);
      return;
    }
    try {
      await loadScriptOnce(url, options);
    } catch (error) {
      const moduleUrl = toModuleUrl(url);
      if (moduleUrl && moduleUrl !== url) {
        await loadModuleOnce(moduleUrl);
        return;
      }
      throw error;
    }
  };

  const dataUrlToBlob = (dataUrl) => {
    const [header, data] = dataUrl.split(',');
    const mime = header.match(/:(.*?);/)[1];
    const binary = atob(data);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      array[i] = binary.charCodeAt(i);
    }
    return new Blob([array], { type: mime });
  };

  const blobToDataUrl = (blob) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });

  const findInputs = (sectionId) => ({
    designJson: document.getElementById(`CustomizerDesignJson-${sectionId}`),
    previewUrl: document.getElementById(`CustomizerDesignPreview-${sectionId}`),
    designId: document.getElementById(`CustomizerDesignId-${sectionId}`)
  });

  // Get or create a hidden cart input for a specific placement inside the product form
  const getOrCreatePlacementInput = (productFormId, placement, placementName) => {
    const form = document.getElementById(productFormId);
    if (!form) return null;
    const key = `pcd-input-${placement}`;
    let input = form.querySelector(`[data-${key}]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[Design ${placementName}]`;
      input.setAttribute(`data-${key}`, '');
      form.appendChild(input);
    }
    return input;
  };

  const bridge = window.ProductCustomizerBridge || {
    _instances: new Map(),
    register(sectionId, data) {
      const current = this._instances.get(sectionId) || {};
      this._instances.set(sectionId, { ...current, ...data });
    },
    setDesign(sectionId, payload) {
      const inputs = findInputs(sectionId);
      if (!inputs.designJson || !inputs.previewUrl || !inputs.designId) return;

      const designJson = payload?.designJson || '';
      const previewUrl = payload?.previewUrl || '';
      const designId = payload?.designId || '';

      inputs.designJson.value = designJson;
      inputs.previewUrl.value = previewUrl;
      inputs.designId.value = designId;

      const instance = this._instances.get(sectionId);
      if (instance?.previewEl && previewUrl) {
        instance.previewEl.src = previewUrl;
        instance.previewEl.hidden = false;
      }

      if (instance?.errorEl) {
        instance.errorEl.hidden = true;
      }
    }
  };

  window.ProductCustomizerBridge = bridge;

  const initCustomizer = async (wrapper) => {
    const sectionId = wrapper.dataset.sectionId;
    const productId = wrapper.dataset.productId;
    const productFormId = wrapper.dataset.productFormId;
    const editorScriptUrl = wrapper.dataset.editorScriptUrl;
    const rawEditorConfig = parseJson(wrapper.dataset.editorConfig, 'editor config');
    const editorConfig = rawEditorConfig || { engine: 'imgly' };
    const variantImages = parseJson(wrapper.dataset.variantImages, 'variant images') || {};
    const defaultImageUrl = wrapper.dataset.defaultImageUrl || '';
    const uploadEndpoint = wrapper.dataset.uploadEndpoint;
    const saveMode = wrapper.dataset.saveMode || 'local';
    const requireDesign = wrapper.dataset.requireDesign === 'true';
    const previewEl = wrapper.querySelector('.product-customizer__preview');
    const errorEl = wrapper.querySelector('[data-customizer-error]');
    const modal = wrapper.querySelector('[data-customizer-modal]');
    const openButtons = wrapper.querySelectorAll('[data-customizer-open]');
    const closeButtons = wrapper.querySelectorAll('[data-customizer-close]');
    const modalTitle = modal?.querySelector('[data-pcd-modal-title]');

    // Per-placement state
    let currentPlacement = 'front';
    let currentPlacementName = 'Front';
    const placementDesigns = new Map(); // placement key → { snapshot, previewUrl }
    const getEditorContainer = () =>
      modal?.querySelector('.product-customizer__editor') ||
      wrapper.querySelector('.product-customizer__editor');

    const normalizeImageUrl = (url) => {
      if (!url) return '';
      if (url.startsWith('//')) return `https:${url}`;
      if (url.startsWith('/')) return `${window.location.origin}${url}`;
      return url;
    };

    const getFallbackImageUrl = () =>
      defaultImageUrl || Object.values(variantImages || {}).find(Boolean) || editorConfig?.baseImageUrl || '';

    const getVariantImageUrl = (variantId) =>
      normalizeImageUrl(variantImages?.[variantId] || getFallbackImageUrl() || '');

    const getActiveVariantId = () => {
      const form = productFormId ? document.getElementById(productFormId) : null;
      const variantIdField = form?.querySelector('.product-variant-id, [name="id"]');
      return variantIdField?.value || '';
    };

    const getActiveGalleryImage = () => {
      const activeSlide = document.querySelector('.product__media-item.is-active img, .product__media-list li.is-active img');
      if (activeSlide) return normalizeImageUrl(activeSlide.src || activeSlide.getAttribute('src'));
      return '';
    };

    bridge.register(sectionId, {
      wrapper,
      previewEl,
      errorEl,
      getVariantImageUrl
    });

    if (modal && document.body) {
      if (!document.body.contains(modal)) {
        document.body.appendChild(modal);
      } else if (modal.parentElement !== document.body) {
        document.body.appendChild(modal);
      }
    }

    const getErrorTarget = () =>
      errorEl || modal?.querySelector('[data-customizer-error]') || wrapper.querySelector('[data-customizer-error]');

    const showError = (message) => {
      const target = getErrorTarget();
      if (!target) return;
      target.hidden = false;
      target.textContent = message;
    };

    // Store SDK instance and current image URL for reloading
    let currentImageUrl = null;
    let sdkInstance = null;

    if (modal && openButtons.length) {
      const openEditorForPlacement = async (btn) => {
        // Don't open if the button is disabled (placement not selected)
        if (btn?.disabled) return;

        const placement     = btn?.dataset?.pcdPlacement     || 'front';
        const placementName = btn?.dataset?.pcdName          || 'Front';
        const placementImg  = normalizeImageUrl(btn?.dataset?.pcdImage || '');

        currentPlacement     = placement;
        currentPlacementName = placementName;

        // Per-placement image: use the explicit image on the button; fall back to variant/gallery
        const activeVariantId   = getActiveVariantId();
        const galleryImage      = getActiveGalleryImage();
        const fallbackImageUrl  = galleryImage || getVariantImageUrl(activeVariantId) || defaultImageUrl;
        const activeImageUrl    = placementImg || fallbackImageUrl;

        // Update modal title
        if (modalTitle) modalTitle.textContent = `Customize — ${placementName}`;

        modal.hidden = false;
        document.body.classList.add('product-customizer--open');

        const imageChanged = currentImageUrl && currentImageUrl !== activeImageUrl;

        if (!wrapper.dataset.customizerInitialized) {
          wrapper.dataset.customizerInitialized = 'true';
          currentImageUrl = activeImageUrl;

          await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

          const shouldLoadImgly =
            editorConfig?.engine === 'imgly' || editorConfig?.license || editorScriptUrl?.includes('cesdk');
          const preferGlobal = editorConfig?.preferGlobal !== false;
          const forceScriptUrl = editorConfig?.forceScriptUrl === true;
          let scriptUrl = editorScriptUrl || DEFAULT_IMGLY_SCRIPT_URL;
          let scriptType = editorConfig?.scriptType || '';
          if (shouldLoadImgly) {
            if (preferGlobal && !forceScriptUrl) {
              scriptUrl = DEFAULT_IMGLY_SCRIPT_URL;
              scriptType = '';
            }
            try {
              await loadImglySdk(scriptUrl, scriptType ? { type: scriptType } : {});
            } catch (error) {
              console.warn('[ProductCustomizer] Failed to load CreativeEditorSDK.', error);
              showError('Unable to load the editor. Check the browser console for more details.');
              return;
            }
          }

          const resolvedConfig = { ...editorConfig, engine: editorConfig?.engine || 'imgly', baseImageUrl: activeImageUrl };
          const detail = {
            sectionId, productId, productFormId,
            editorConfig: resolvedConfig,
            uploadEndpoint, saveMode,
            baseImageUrl: activeImageUrl,
            getVariantImageUrl,
            container: getEditorContainer(),
            getCurrentPlacement: () => currentPlacement,
            getCurrentPlacementName: () => currentPlacementName,
            getPlacementDesigns: () => placementDesigns,
            onSdkReady: (sdk) => { sdkInstance = sdk; }
          };
          wrapper.dispatchEvent(new CustomEvent('product-customizer:mount', { detail }));
          document.dispatchEvent(new CustomEvent('product-customizer:mount', { detail }));

          if (resolvedConfig?.engine === 'konva') initKonvaCustomizer(detail);
          if (resolvedConfig?.engine === 'imgly')  initImglyCustomizer(detail);

        } else {
          // Editor already initialized — switch placement
          currentImageUrl = activeImageUrl;
          const inst = bridge._instances.get(sectionId);

          // Load saved design for this placement (if any) or clear canvas
          // Pass activeImageUrl directly so the correct image is used without re-querying
          if (inst?.loadPlacementDesign) {
            inst.loadPlacementDesign(placement, activeImageUrl);
          } else if (inst?.updateBaseImage) {
            inst.updateBaseImage(activeImageUrl);
          }

          if (sdkInstance && imageChanged) {
            try { await sdkInstance.createFromImage(activeImageUrl); } catch(e) { /* ignore */ }
          }
        }

        // Rescale on every open
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        const inst = bridge._instances.get(sectionId);
        if (inst?.konvaRescale) inst.konvaRescale();
      };

      openButtons.forEach((btn) => {
        btn.addEventListener('click', () => openEditorForPlacement(btn));
      });

      closeButtons.forEach((button) => {
        button.addEventListener('click', () => {
          modal.hidden = true;
          document.body.classList.remove('product-customizer--open');
        });
      });

      // ── Placement card interactions ────────────────────────────────────────────
      // Checkbox: toggle row selection and remove cart input when unchecked
      wrapper.querySelectorAll('[data-pcd-checkbox]').forEach((chk) => {
        // Front is disabled (always included) - skip
        if (chk.closest('[data-pcd-row="front"]')) {
          chk.disabled = true;
          return;
        }
        chk.addEventListener('change', () => {
          const placement = chk.dataset.pcdCheckbox;
          const row = wrapper.querySelector(`[data-pcd-row="${placement}"]`);
          row?.classList.toggle('pcd-row--selected', chk.checked);
          const btn = row?.querySelector('[data-customizer-open]');
          if (chk.checked) {
            // Enable Customize button when placement is selected
            btn?.removeAttribute('disabled');
          } else {
            // Disable Customize button and clear design when placement is deselected
            btn?.setAttribute('disabled', '');
            btn?.classList.remove('pcd-customize-btn--done');
            placementDesigns.delete(placement);
            const form = document.getElementById(productFormId);
            form?.querySelector(`[data-pcd-input-${placement}]`)?.remove();
          }
        });
      });

      // Note toggle
      wrapper.querySelectorAll('[data-pcd-note-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const placement = btn.dataset.pcdNoteToggle;
          const noteRow = wrapper.querySelector(`[data-pcd-note-row="${placement}"]`);
          if (noteRow) {
            noteRow.hidden = !noteRow.hidden;
            btn.textContent = noteRow.hidden ? 'Add note' : 'Hide note';
          }
        });
      });
    }

    if (requireDesign && productFormId) {
      const form = document.getElementById(productFormId);
      if (form) {
        form.addEventListener('submit', (event) => {
          const hasDesign = placementDesigns.size > 0;
          if (!hasDesign) {
            event.preventDefault();
            if (errorEl) {
              errorEl.hidden = false;
            }
            wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
    }

    // ── Safety net: strip data URLs before any cart form submit ──────────────
    // The token input and localStorage entry are written at save time (in
    // saveDesign) so Dawn's AJAX FormData already includes the token.
    // This listener just ensures no stray data URL ever reaches /cart/add.
    if (productFormId && saveMode === 'backend') {
      const form = document.getElementById(productFormId);
      if (form && !form.dataset.pcdUploadIntercepted) {
        form.dataset.pcdUploadIntercepted = 'true';
        form.addEventListener('submit', () => {
          form.querySelectorAll('input[type="hidden"]').forEach(input => {
            if (input.value.startsWith('data:')) input.value = '';
          });
        });
      }
    }

    if (!modal && !wrapper.dataset.customizerInitialized) {
      wrapper.dataset.customizerInitialized = 'true';
      const shouldLoadImgly =
        editorConfig?.engine === 'imgly' || editorConfig?.license || editorScriptUrl?.includes('cesdk');
      const preferGlobal = editorConfig?.preferGlobal !== false;
      const forceScriptUrl = editorConfig?.forceScriptUrl === true;
      let scriptUrl = editorScriptUrl || DEFAULT_IMGLY_SCRIPT_URL;
      let scriptType = editorConfig?.scriptType || '';
      if (shouldLoadImgly) {
        if (preferGlobal && !forceScriptUrl) {
          scriptUrl = DEFAULT_IMGLY_SCRIPT_URL;
          scriptType = '';
        }
        await loadImglySdk(scriptUrl, scriptType ? { type: scriptType } : {});
      }

      const baseImageUrl = getVariantImageUrl(getActiveVariantId());
      const resolvedConfig = {
        ...editorConfig,
        engine: editorConfig?.engine || 'imgly',
        baseImageUrl
      };
      const detail = {
        sectionId,
        productId,
        productFormId,
        editorConfig: resolvedConfig,
        uploadEndpoint,
        saveMode,
        baseImageUrl,
        getVariantImageUrl,
        container: getEditorContainer()
      };

      wrapper.dispatchEvent(new CustomEvent('product-customizer:mount', { detail }));
      document.dispatchEvent(new CustomEvent('product-customizer:mount', { detail }));

      if (resolvedConfig?.engine === 'konva') {
        initKonvaCustomizer(detail);
      }
      if (resolvedConfig?.engine === 'imgly') {
        initImglyCustomizer(detail);
      }
    }

    if (productFormId) {
      const form = document.getElementById(productFormId);
      // Accept either class or name selector — Dawn uses .product-variant-id
      const variantInput = form?.querySelector('.product-variant-id, input[name="id"]') || null;

      // Parse per-variant placement images map built in Liquid
      let placementImagesMap = {};
      try {
        placementImagesMap = JSON.parse(wrapper.dataset.placementImages || '{}');
      } catch(e) {
        console.warn('[ProductCustomizer] Could not parse placement images map', e);
      }
      const updatePlacementCard = (variantId) => {
        const images = placementImagesMap[String(variantId)];
        if (!images) return;
        ['front', 'back', 'left', 'right'].forEach((placement) => {
          const fullUrl = images[placement];
          if (!fullUrl) return;
          const absUrl  = fullUrl.startsWith('//') ? 'https:' + fullUrl : fullUrl;
          const thumbUrl = absUrl.replace(/([?&]width=)\d+/, '$1120');
          const btn = wrapper.querySelector(`[data-pcd-placement="${placement}"][data-customizer-open]`);
          if (btn) btn.dataset.pcdImage = absUrl;
          const img = wrapper.querySelector(`[data-pcd-thumb="${placement}"]`);
          if (img) { img.src = thumbUrl; img.hidden = false; }
        });
      };

      const handleVariantChange = (variantId) => {
        if (!variantId) return;
        const frontUrl = getVariantImageUrl(String(variantId)) || defaultImageUrl;
        const instance = bridge._instances.get(sectionId);
        if (instance?.updateBaseImage) instance.updateBaseImage(frontUrl);
        if (instance?.hasDesign) {
          bridge.setDesign(sectionId, { designJson: '', previewUrl: '', designId: '' });
          instance.hasDesign = false;
        }
        updatePlacementCard(variantId);
      };

      // ── Layer 1: Dawn pub/sub ─────────────────────────────────────────────────
      if (typeof subscribe === 'function') {
        subscribe('variant-change', (event) => {
          if (event?.data?.sectionId !== sectionId) return;
          const vid = event?.data?.variant?.id;
          if (vid) handleVariantChange(String(vid));
        });
      }

      // ── Layer 2: MutationObserver on variant-selects ─────────────────────────
      // Dawn replaces variant-selects children after each async fetch, so this
      // fires reliably regardless of pub/sub timing.
      const productInfoEl = document.querySelector(`product-info[data-section="${sectionId}"]`);
      const variantSelectsEl = productInfoEl?.querySelector('variant-selects');
      if (variantSelectsEl) {
        let lastObservedVariantId = variantInput?.value || '';
        const variantObserver = new MutationObserver(() => {
          const selectedEl = variantSelectsEl.querySelector('[data-selected-variant]');
          if (!selectedEl) return;
          try {
            const variant = JSON.parse(selectedEl.innerHTML || 'null');
            const vid = String(variant?.id || '');
            if (vid && vid !== lastObservedVariantId) {
              lastObservedVariantId = vid;
              handleVariantChange(vid);
            }
          } catch(e) {}
        });
        variantObserver.observe(variantSelectsEl, { childList: true, subtree: true, characterData: true });
      }

      // ── Layer 3: direct change listener on hidden variant input ───────────────
      if (variantInput) {
        variantInput.addEventListener('change', () => handleVariantChange(variantInput.value));
      }

      // Sync immediately on init
      const initialVariantId = variantInput?.value
        || new URLSearchParams(location.search).get('variant')
        || '';
      if (initialVariantId) handleVariantChange(initialVariantId);
    }
  };

  const initAll = () => {
    document.querySelectorAll(CUSTOMIZER_SELECTOR).forEach((wrapper) => {
      initCustomizer(wrapper);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('product-customizer:save', (event) => {
    const { sectionId, designJson, previewUrl, designId } = event.detail || {};
    if (!sectionId) return;
    bridge.setDesign(sectionId, { designJson, previewUrl, designId });
  });

  const initKonvaCustomizer = async (detail) => {
    const { sectionId, editorConfig, uploadEndpoint, container, productFormId, saveMode, baseImageUrl } = detail;
    if (!container) return;

    // ── Parse patch options from data attributes ─────────────────────────────────
    // Falls back to built-in defaults so the panel always appears even if the
    // merchant hasn't saved the theme-customizer settings yet (Shopify only
    // applies schema defaults when a block is first created, not retroactively).
    const pcdEl = container.closest('.product-customizer');
    const _parseOpts = (attr, fallback) => {
      const raw = pcdEl?.dataset[attr] || '';
      if (raw) { try { const v = JSON.parse(raw); if (v.length) return v; } catch (_) {} }
      return fallback;
    };

    const decorationMethods = _parseOpts('decorationMethods', [
      { label: 'UV3D Printed Patch',               value: 'uv3d'                        },
      { label: 'Laser Engraved Leatherette Patch', value: 'laser', hasColor: true       },
    ]);
    const leatheretteColors = _parseOpts('leatheretteColors', [
      { label: 'Walnut',      value: 'walnut',     hex: '#7A4B1E' },
      { label: 'Rawhide',     value: 'rawhide',    hex: '#C4A46B' },
      { label: 'Dark Brown',  value: 'dark-brown', hex: '#3B1C08' },
      { label: 'Grey',        value: 'grey',       hex: '#7A7A7A' },
    ]);
    const patchShapes = _parseOpts('patchShapes', [
      { label: 'Rectangle',       value: 'rectangle'    },
      { label: 'Rounded Rect',    value: 'rounded-rect' },
      { label: 'Circle',          value: 'circle'       },
      { label: 'Oval',            value: 'oval'         },
      { label: 'Shield',          value: 'shield'       },
      { label: 'Hexagon',         value: 'hexagon'      },
      { label: 'Contoured Design',value: 'contoured'    },
    ]);
    const patchSizes = _parseOpts('patchSizes', [
      { label: '3" × 2"',   value: '3x2'      },
      { label: '3.5" × 2"', value: '3.5x2'    },
      { label: '2.5" × 2"', value: '2.5x2'    },
      { label: '2" Circle',  value: '2-circle' },
      { label: 'Custom Size',value: 'custom'   },
    ]);

    // Track selected patch options (start with first in each list)
    let selectedDecoration = decorationMethods[0] || null;
    let selectedShape      = patchShapes[0]       || null;
    let selectedSize       = patchSizes[0]         || null;
    // Leatherette color is only active when a decoration with hasColor:true is selected
    let selectedColor = selectedDecoration?.hasColor ? (leatheretteColors[0] || null) : null;
    // patchArea is assigned after computePatchArea is defined (below, after DESIGN_W/PRINT_AREA)
    let patchArea          = null;

    // ── Load Konva from CDN if not already present ──────────────────────────────
    if (!window.Konva) {
      try {
        await loadScriptOnce('https://unpkg.com/konva@9.3.16/konva.min.js');
      } catch (e) {
        console.warn('[ProductCustomizer] Failed to load Konva.js', e);
        const errEl = container.closest('.product-customizer')?.querySelector('[data-customizer-error]');
        if (errEl) { errEl.hidden = false; errEl.textContent = 'Editor failed to load. Please refresh the page.'; }
        return;
      }
    }

    const K = window.Konva;
    const config = editorConfig || {};
    const DESIGN_W = config.width || 700;
    const DESIGN_H = config.height || 500;
    // Optional printable area overlay: config.printArea = {x, y, width, height}
    const PRINT_AREA = config.printArea || null;

    // Compute the base patch bounds from a size value like '3x2', '3.5x2', '2-circle'.
    // Parses the aspect ratio and fits it to ~38% of canvas (centered on hat front).
    // Falls back to PRINT_AREA when configured so merchant settings always win.
    const computePatchArea = (sizeVal) => {
      if (PRINT_AREA) return PRINT_AREA;
      let ar = 1.5; // default: 3×2 ratio
      if (sizeVal) {
        const m = String(sizeVal).match(/^([\d.]+)[x×]([\d.]+)/i);
        if (m) {
          ar = parseFloat(m[1]) / parseFloat(m[2]);
        } else if (/circle/i.test(sizeVal)) {
          ar = 1; // circle → square bounding box
        }
      }
      // Keep the patch proportional but small — ~38% of the shorter canvas dimension
      const maxW = DESIGN_W * 0.38;
      const maxH = DESIGN_H * 0.38;
      let pw, ph;
      if (ar >= maxW / maxH) { pw = maxW; ph = pw / ar; }
      else { ph = maxH; pw = ph * ar; }
      return {
        x: Math.round((DESIGN_W - pw) / 2),
        y: Math.round((DESIGN_H - ph) / 2),
        width: Math.round(pw),
        height: Math.round(ph),
      };
    };

    // User-adjustable position offset (arrows) and scale (+ / -)
    let patchOffsetX = 0;
    let patchOffsetY = 0;
    let patchScale   = 1.0;

    // Returns the effective patch area including offset and scale
    const effectivePatchArea = () => {
      const pw = Math.round(patchArea.width  * patchScale);
      const ph = Math.round(patchArea.height * patchScale);
      // scale from centre, then apply offset
      const cx = patchArea.x + patchArea.width  / 2 + patchOffsetX;
      const cy = patchArea.y + patchArea.height / 2 + patchOffsetY;
      return {
        x: Math.round(cx - pw / 2),
        y: Math.round(cy - ph / 2),
        width:  pw,
        height: ph,
      };
    };

    // Now that computePatchArea is defined, initialize patchArea from the default size
    patchArea = computePatchArea(selectedSize?.value);

    // ── Scaffold HTML ────────────────────────────────────────────────────────────
    container.innerHTML = '';
    container.classList.add('product-customizer__konva');

    const wrap = document.createElement('div');
    wrap.className = 'pck-wrap';
    container.appendChild(wrap);

    // Toolbar
    const toolbar = document.createElement('div');
    toolbar.className = 'pck-toolbar';
    toolbar.innerHTML = `
      <div class="pck-tools">
        <button type="button" class="pck-btn" data-action="add-text" title="Add text layer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M5 4v3h5.5v12h3V7H19V4z"/></svg>
          <span>Text</span>
        </button>
        <button type="button" class="pck-btn" data-action="upload-image" title="Upload image or logo">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z"/></svg>
          <span>Image</span>
        </button>
        <button type="button" class="pck-btn" data-action="toggle-shapes" title="Add shape" id="pck-shapes-btn-${sectionId}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M11 13.5v8H4v-8h7zm1-11.5L23 20H1L12 2zm7 11.5h4v8h-4v-8z"/></svg>
          <span>Shapes</span>
        </button>
      </div>
      <div class="pck-separator" aria-hidden="true"></div>
      <div class="pck-props" id="pck-props-${sectionId}"></div>
      <div class="pck-separator" aria-hidden="true"></div>
      <div class="pck-actions">
        <button type="button" class="pck-btn" data-action="undo" title="Undo (Ctrl+Z)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/></svg>
          Undo
        </button>
        <button type="button" class="pck-btn" data-action="redo" title="Redo (Ctrl+Y)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z"/></svg>
          Redo
        </button>
        <button type="button" class="pck-btn pck-btn--danger" data-action="delete" title="Delete selected (Del)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
          Delete
        </button>
      </div>
      <input type="file" accept="image/*" hidden data-upload-input>
    `;

    // Stage container (fills remaining space)
    const stageContainer = document.createElement('div');
    stageContainer.className = 'pck-stage-container';
    const stageDiv = document.createElement('div');
    stageDiv.className = 'pck-stage-inner';
    stageContainer.appendChild(stageDiv);

    wrap.appendChild(toolbar);
    wrap.appendChild(stageContainer);

    // ── Patch options panel ───────────────────────────────────────────────────────
    // Renders Decoration Method / Patch Shape / Patch Size selectors below the canvas.
    // Only sections with at least one option are rendered.
    const optionsPanel = document.createElement('div');
    optionsPanel.className = 'pck-patch-options';

    const buildOptionsSection = (title, items, groupKey, getSelected) => {
      if (!items.length) return '';
      const isShape = groupKey === 'shape';
      return `
        <div class="pck-patch-group">
          <div class="pck-patch-label">${title}</div>
          <div class="pck-patch-choices${isShape ? ' pck-patch-choices--grid' : ''}">
            ${items.map(item => `
              <button type="button"
                class="pck-patch-btn${item.value === getSelected()?.value ? ' pck-patch-btn--active' : ''}"
                data-group="${groupKey}" data-value="${item.value}">
                ${item.label}
              </button>`).join('')}
          </div>
        </div>`;
    };

    // Position controls — arrows + zoom buttons to adjust patch on the hat
    const positionControlsHtml = `
      <div class="pck-patch-group pck-patch-position">
        <div class="pck-patch-label">Patch Position &amp; Size</div>
        <div class="pck-position-controls">
          <button type="button" class="pck-pos-btn" data-move="up"    title="Move up">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 5l8 9H4z"/></svg>
          </button>
          <button type="button" class="pck-pos-btn" data-move="left"  title="Move left">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M5 12l9-8v16z"/></svg>
          </button>
          <button type="button" class="pck-pos-btn pck-pos-btn--center" data-move="center" title="Reset position &amp; size">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><line x1="12" y1="2" x2="12" y2="7"/><line x1="12" y1="17" x2="12" y2="22"/><line x1="2" y1="12" x2="7" y2="12"/><line x1="17" y1="12" x2="22" y2="12"/></svg>
          </button>
          <button type="button" class="pck-pos-btn" data-move="right" title="Move right">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 12l-9 8V4z"/></svg>
          </button>
          <button type="button" class="pck-pos-btn" data-move="down"  title="Move down">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 19l-8-9h16z"/></svg>
          </button>
          <div class="pck-pos-divider"></div>
          <button type="button" class="pck-pos-btn pck-pos-btn--zoom" data-move="zoom-out" title="Shrink patch">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 13H5v-2h14v2z"/></svg>
          </button>
          <button type="button" class="pck-pos-btn pck-pos-btn--zoom" data-move="zoom-in"  title="Grow patch">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
          </button>
        </div>
      </div>`;

    // Color section — only visible when the selected decoration has hasColor:true
    const buildColorSection = () => {
      if (!leatheretteColors.length) return '';
      const hidden = selectedDecoration?.hasColor ? '' : ' hidden';
      return `
        <div class="pck-patch-group pck-color-section"${hidden}>
          <div class="pck-patch-label">Leatherette Color</div>
          <div class="pck-color-choices">
            ${leatheretteColors.map(c => `
              <button type="button"
                class="pck-color-btn${selectedColor?.value === c.value ? ' pck-color-btn--active' : ''}"
                data-group="color" data-value="${c.value}" data-hex="${c.hex}">
                <span class="pck-color-dot" style="background:${c.hex}"></span>
                ${c.label}
              </button>`).join('')}
          </div>
        </div>`;
    };

    optionsPanel.innerHTML =
      positionControlsHtml +
      buildOptionsSection('Decoration Method', decorationMethods, 'decoration', () => selectedDecoration) +
      buildColorSection() +
      buildOptionsSection('Patch Shape',       patchShapes,       'shape',      () => selectedShape) +
      buildOptionsSection('Patch Size',        patchSizes,        'size',       () => selectedSize);

    if (optionsPanel.innerHTML.trim()) wrap.appendChild(optionsPanel);

    // ── Shapes drawer ────────────────────────────────────────────────────────────
    const SHAPE_DEFS = [
      // Solid
      { id: 'rect-s',   label: 'Rectangle', konva: 'Rect',           solid: true,  dash: false },
      { id: 'circle-s', label: 'Circle',    konva: 'Circle',         solid: true,  dash: false },
      { id: 'tri-s',    label: 'Triangle',  konva: 'RegularPolygon', solid: true,  dash: false, sides: 3 },
      { id: 'pent-s',   label: 'Pentagon',  konva: 'RegularPolygon', solid: true,  dash: false, sides: 5 },
      { id: 'star-s',   label: 'Star',      konva: 'Star',           solid: true,  dash: false },
      { id: 'line-s',   label: 'Line',      konva: 'Line',           solid: false, dash: false },
      // Outlined
      { id: 'rect-o',   label: 'Rectangle', konva: 'Rect',           solid: false, dash: false },
      { id: 'circle-o', label: 'Circle',    konva: 'Circle',         solid: false, dash: false },
      { id: 'tri-o',    label: 'Triangle',  konva: 'RegularPolygon', solid: false, dash: false, sides: 3 },
      { id: 'pent-o',   label: 'Pentagon',  konva: 'RegularPolygon', solid: false, dash: false, sides: 5 },
      { id: 'star-o',   label: 'Star',      konva: 'Star',           solid: false, dash: false },
      { id: 'dcircle',  label: 'Dashed',    konva: 'Circle',         solid: false, dash: true  },
    ];

    const SHAPE_THUMBS = {
      'rect-s':   `<svg viewBox="0 0 52 52"><rect x="4" y="11" width="44" height="30" rx="2" fill="#111"/></svg>`,
      'circle-s': `<svg viewBox="0 0 52 52"><circle cx="26" cy="26" r="20" fill="#111"/></svg>`,
      'tri-s':    `<svg viewBox="0 0 52 52"><polygon points="26,4 50,48 2,48" fill="#111"/></svg>`,
      'pent-s':   `<svg viewBox="0 0 52 52"><polygon points="26,2 50,19 41,48 11,48 2,19" fill="#111"/></svg>`,
      'star-s':   `<svg viewBox="0 0 52 52"><polygon points="26,2 32,20 50,20 36,32 42,50 26,39 10,50 16,32 2,20 20,20" fill="#111"/></svg>`,
      'line-s':   `<svg viewBox="0 0 52 52"><line x1="4" y1="26" x2="48" y2="26" stroke="#111" stroke-width="4" stroke-linecap="round"/></svg>`,
      'rect-o':   `<svg viewBox="0 0 52 52"><rect x="4" y="11" width="44" height="30" rx="2" fill="none" stroke="#111" stroke-width="3"/></svg>`,
      'circle-o': `<svg viewBox="0 0 52 52"><circle cx="26" cy="26" r="20" fill="none" stroke="#111" stroke-width="3"/></svg>`,
      'tri-o':    `<svg viewBox="0 0 52 52"><polygon points="26,4 50,48 2,48" fill="none" stroke="#111" stroke-width="3"/></svg>`,
      'pent-o':   `<svg viewBox="0 0 52 52"><polygon points="26,2 50,19 41,48 11,48 2,19" fill="none" stroke="#111" stroke-width="3"/></svg>`,
      'star-o':   `<svg viewBox="0 0 52 52"><polygon points="26,2 32,20 50,20 36,32 42,50 26,39 10,50 16,32 2,20 20,20" fill="none" stroke="#111" stroke-width="3"/></svg>`,
      'dcircle':  `<svg viewBox="0 0 52 52"><circle cx="26" cy="26" r="20" fill="none" stroke="#111" stroke-width="3" stroke-dasharray="6 4"/></svg>`,
    };

    const shapesDrawer = document.createElement('div');
    shapesDrawer.className = 'pck-shapes-drawer';
    shapesDrawer.hidden = true;
    shapesDrawer.innerHTML = `
      <div class="pck-shapes-drawer__inner">
        <div class="pck-shapes-drawer__header">
          <span>Shapes</span>
          <button type="button" class="pck-btn pck-btn--icon-only pck-btn--sm" data-action="close-shapes" aria-label="Close shapes panel">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
          </button>
        </div>
        <div class="pck-shapes-section-title">Solid</div>
        <div class="pck-shapes-grid">
          ${SHAPE_DEFS.filter(d => d.solid || d.id === 'line-s').map(d =>
            `<button type="button" class="pck-shape-item" data-shape="${d.id}" title="${d.label}">${SHAPE_THUMBS[d.id]}</button>`
          ).join('')}
        </div>
        <div class="pck-shapes-section-title" style="margin-top:12px">Outlined</div>
        <div class="pck-shapes-grid">
          ${SHAPE_DEFS.filter(d => !d.solid && d.id !== 'line-s').map(d =>
            `<button type="button" class="pck-shape-item" data-shape="${d.id}" title="${d.label}">${SHAPE_THUMBS[d.id]}</button>`
          ).join('')}
        </div>
      </div>
    `;
    stageContainer.appendChild(shapesDrawer);

    const shapesBtn = toolbar.querySelector(`#pck-shapes-btn-${sectionId}`);

    // ── Konva Stage ──────────────────────────────────────────────────────────────
    const stage = new K.Stage({ container: stageDiv, width: DESIGN_W, height: DESIGN_H });

    // bgLayer: product image + print area guide (non-interactive)
    const bgLayer = new K.Layer({ listening: false });
    // patchBgLayer: white background in the selected patch shape
    const patchBgLayer = new K.Layer({ listening: false });
    // designLayer: all user-added elements + transformer (clipped to patch shape)
    const designLayer = new K.Layer();
    stage.add(bgLayer);
    stage.add(patchBgLayer);
    stage.add(designLayer);

    // ── Patch shape clip helpers ──────────────────────────────────────────────────
    // Returns a Konva clipFunc that draws the selected patch shape within `area`.
    // area defaults to the full canvas when omitted.
    const makePatchClipFunc = (shape, area) => (ctx) => {
      const x  = area ? area.x      : 0;
      const y  = area ? area.y      : 0;
      const w  = area ? area.width  : DESIGN_W;
      const h  = area ? area.height : DESIGN_H;
      const cx = x + w / 2, cy = y + h / 2;
      ctx.beginPath();
      switch (shape) {
        case 'rounded-rect': {
          const r = Math.min(w, h) * 0.12;
          ctx.moveTo(x + r, y); ctx.lineTo(x + w - r, y); ctx.arcTo(x + w, y, x + w, y + r, r);
          ctx.lineTo(x + w, y + h - r); ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
          ctx.lineTo(x + r, y + h); ctx.arcTo(x, y + h, x, y + h - r, r);
          ctx.lineTo(x, y + r); ctx.arcTo(x, y, x + r, y, r);
          break;
        }
        case 'circle':
          ctx.arc(cx, cy, Math.min(w, h) / 2, 0, Math.PI * 2);
          break;
        case 'oval':
          ctx.ellipse(cx, cy, w / 2, h / 2, 0, 0, Math.PI * 2);
          break;
        case 'shield': {
          // Flat top → straight sides → quadratic curves to bottom tip
          const sh = h * 0.58;
          ctx.moveTo(x, y); ctx.lineTo(x + w, y);
          ctx.lineTo(x + w, y + sh);
          ctx.quadraticCurveTo(x + w, y + h, cx, y + h);
          ctx.quadraticCurveTo(x, y + h, x, y + sh);
          break;
        }
        case 'hexagon': {
          const r = Math.min(w, h) / 2;
          for (let i = 0; i < 6; i++) {
            const a = (Math.PI / 3) * i - Math.PI / 6;
            i === 0 ? ctx.moveTo(cx + r * Math.cos(a), cy + r * Math.sin(a))
                    : ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
          }
          break;
        }
        default: // rectangle, contoured, unknown → full area bounds
          ctx.rect(x, y, w, h);
      }
      ctx.closePath();
    };

    // Builds the patch background Konva shape. fill defaults to white but changes
    // to the selected leatherette color when that decoration method is active.
    const buildPatchBg = (shape, area, fill = '#ffffff') => new K.Shape({
      sceneFunc: (ctx, node) => {
        makePatchClipFunc(shape, area)(ctx);
        ctx.fillStrokeShape(node);
      },
      fill,
      listening: false,
    });

    // Apply (or remove) the patch shape.
    // Shows a white patch background at the patchArea position and clips the
    // design layer to that area with the selected shape.
    // Hat image (bgLayer) remains visible outside the patch area.
    const applyPatchShape = (shape) => {
      const area = effectivePatchArea();
      const patchFill = selectedColor?.hex || '#ffffff';
      patchBgLayer.destroyChildren();
      if (shape && shape !== 'contoured') {
        patchBgLayer.add(buildPatchBg(shape, area, patchFill));
        designLayer.clipFunc(makePatchClipFunc(shape, area));
      } else {
        // Contoured / no shape — full canvas, no clip
        designLayer.clipFunc(null);
      }
      patchBgLayer.draw();
      designLayer.draw();
    };

    // Apply initial shape
    if (selectedShape) applyPatchShape(selectedShape.value);

    // Load product background image — cancels any in-flight load on rapid variant switches
    let bgLoadSeq = 0;
    const loadBg = (url) => {
      const seq = ++bgLoadSeq;

      const applyBg = (imgEl) => {
        if (seq !== bgLoadSeq) return; // superseded by a newer call
        bgLayer.destroyChildren();
        if (imgEl) {
          const scale = Math.max(DESIGN_W / imgEl.width, DESIGN_H / imgEl.height);
          const w = imgEl.width * scale;
          const h = imgEl.height * scale;
          bgLayer.add(new K.Image({
            image: imgEl,
            x: (DESIGN_W - w) / 2,
            y: (DESIGN_H - h) / 2,
            width: w,
            height: h,
            listening: false
          }));
        } else {
          bgLayer.add(new K.Rect({ x: 0, y: 0, width: DESIGN_W, height: DESIGN_H, fill: '#ffffff', listening: false }));
        }
        if (PRINT_AREA) {
          bgLayer.add(new K.Rect({
            x: PRINT_AREA.x, y: PRINT_AREA.y,
            width: PRINT_AREA.width, height: PRINT_AREA.height,
            stroke: '#0070f3', strokeWidth: 1.5, dash: [6, 3],
            fill: 'rgba(0,112,243,0.04)', listening: false
          }));
        }
        bgLayer.draw();
      };

      if (!url) { applyBg(null); return; }
      const imgEl = new Image();
      imgEl.crossOrigin = 'anonymous';
      imgEl.onload = () => applyBg(imgEl);
      imgEl.onerror = () => {
        // CORS retry: if anonymous load fails, try without crossOrigin so the
        // background at least renders (canvas will be tainted for export though)
        const fallback = new Image();
        fallback.onload = () => applyBg(fallback);
        fallback.onerror = () => applyBg(null);
        fallback.src = url;
      };
      // Cache-bust so the browser fetches with CORS headers even if the gallery
      // already cached the same URL without them (tainted-canvas prevention)
      const sep = url.includes('?') ? '&' : '?';
      imgEl.src = url + sep + '_cb=' + bgLoadSeq;
    };
    loadBg(baseImageUrl || '');

    // Transformer
    const tr = new K.Transformer({
      rotateEnabled: true,
      keepRatio: false,
      boundBoxFunc: (oldBox, newBox) =>
        Math.abs(newBox.width) < 10 || Math.abs(newBox.height) < 10 ? oldBox : newBox
    });
    designLayer.add(tr);

    // ── Scale stage to fit available container ───────────────────────────────────
    let currentScale = 1;

    const rescale = () => {
      const cw = stageContainer.clientWidth;
      const ch = stageContainer.clientHeight;
      if (!cw || !ch) return;
      // Fill the available width; if the resulting height exceeds the container, constrain by height instead
      const s = Math.min(cw / DESIGN_W, ch / DESIGN_H);
      if (Math.abs(s - currentScale) < 0.001) return;
      currentScale = s;
      stage.width(Math.round(DESIGN_W * s));
      stage.height(Math.round(DESIGN_H * s));
      stage.scaleX(s);
      stage.scaleY(s);
      stage.draw();
    };

    const sizeEditorAndRescale = () => {
      // Stage container height is now driven by CSS (62vh). Just rescale to fit.
      rescale();
    };

    // Double-RAF: first pass lets the browser lay out the options panel,
    // second pass reads its correct offsetHeight so the stage isn't over-sized.
    requestAnimationFrame(() => {
      sizeEditorAndRescale();
      requestAnimationFrame(sizeEditorAndRescale);
    });
    window.addEventListener('resize', sizeEditorAndRescale);

    // ── Selection ─────────────────────────────────────────────────────────────────
    let selectedNode = null;

    const selectNode = (node) => {
      selectedNode = node;
      tr.nodes(node ? [node] : []);
      designLayer.draw();
      renderPropsPanel();
    };

    stage.on('click tap', (e) => {
      if (e.target === stage) { selectNode(null); return; }
      const node = e.target;
      // Ignore transformer handles and bg layer children
      if (node.getLayer?.() === bgLayer || !node.draggable?.()) return;
      selectNode(node);
    });

    // ── History ───────────────────────────────────────────────────────────────────
    const MAX_HIST = 30;
    let history = [];
    let histIdx = -1;

    const snapshotDesign = () => {
      // Serialise only design nodes (exclude transformer)
      const nodes = designLayer.getChildren((n) => n !== tr);
      return JSON.stringify(nodes.map((n) => n.toObject()));
    };

    const pushHistory = () => {
      if (histIdx < history.length - 1) history = history.slice(0, histIdx + 1);
      history.push(snapshotDesign());
      if (history.length > MAX_HIST) { history.shift(); } else { histIdx++; }
    };

    const restoreSnapshot = (snap) => {
      designLayer.getChildren((n) => n !== tr).forEach((n) => n.destroy());
      tr.nodes([]);
      selectedNode = null;
      const nodes = JSON.parse(snap);
      nodes.forEach((obj) => {
        let node;
        try { node = K.Node.create(obj); } catch (e) { return; }
        if (node.className === 'Text') attachTextEvents(node);
        attachNodeEvents(node);
        designLayer.add(node);
      });
      designLayer.draw();
      renderPropsPanel();
    };

    const undo = () => {
      if (histIdx <= 0) return;
      histIdx--;
      restoreSnapshot(history[histIdx]);
    };

    const redo = () => {
      if (histIdx >= history.length - 1) return;
      histIdx++;
      restoreSnapshot(history[histIdx]);
    };

    // Initial snapshot
    pushHistory();

    // ── Inline text editing ───────────────────────────────────────────────────────
    let activeTextarea = null;

    const startTextEdit = (textNode) => {
      if (activeTextarea) return;
      tr.hide();
      textNode.hide();
      designLayer.draw();

      const textarea = document.createElement('textarea');
      textarea.className = 'pck-text-edit';
      textarea.value = textNode.text();
      textarea.spellcheck = false;

      // Position textarea over the text node.
      // getAbsolutePosition() already incorporates the stage scale transform,
      // so its x/y values are in screen pixels — do NOT multiply by s again.
      const s = currentScale;
      const absPos = textNode.getAbsolutePosition();
      const stageRect = stageDiv.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const left = stageRect.left - containerRect.left + absPos.x;
      const top  = stageRect.top  - containerRect.top  + absPos.y;

      textarea.style.cssText = `
        position: absolute;
        left: ${left}px; top: ${top}px;
        width: ${Math.max(textNode.width() * s, 100)}px;
        min-height: ${textNode.fontSize() * s * 1.4}px;
        font-size: ${textNode.fontSize() * s}px;
        font-family: ${textNode.fontFamily()};
        font-weight: ${textNode.fontStyle().includes('bold') ? 'bold' : 'normal'};
        font-style: ${textNode.fontStyle().includes('italic') ? 'italic' : 'normal'};
        color: ${textNode.fill()};
        text-align: ${textNode.align()};
        line-height: 1.2;
        border: 2px dashed #0070f3;
        background: rgba(255,255,255,0.85);
        outline: none; resize: none; overflow: hidden; padding: 2px;
        z-index: 200; box-sizing: border-box; border-radius: 2px;
      `;

      container.style.position = 'relative';
      container.appendChild(textarea);
      textarea.focus();
      textarea.select();
      activeTextarea = textarea;

      // Auto-grow
      const autoSize = () => {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      };
      textarea.addEventListener('input', autoSize);
      autoSize();

      const commit = () => {
        if (!activeTextarea) return;
        const newText = textarea.value.trim();
        if (newText) {
          textNode.text(textarea.value);
          // Re-measure after text change: Konva recalculates width/height
          // so update offsetX/Y to keep the text centered on its anchor point.
          textNode.offsetX(textNode.width()  / 2);
          textNode.offsetY(textNode.height() / 2);
          textNode.show();
          pushHistory();
        } else {
          textNode.destroy();
          selectedNode = null;
          tr.nodes([]);
          pushHistory();
        }
        tr.show();
        designLayer.draw();
        textarea.remove();
        activeTextarea = null;
        renderPropsPanel();
      };

      textarea.addEventListener('blur', commit);
      textarea.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { textarea.value = textNode.text(); textarea.blur(); }
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); textarea.blur(); }
      });
    };

    const attachTextEvents = (node) => {
      node.on('dblclick dbltap', () => {
        selectNode(node);
        startTextEdit(node);
      });
    };

    const attachNodeEvents = (node) => {
      node.on('click tap', (e) => {
        e.cancelBubble = true;
        selectNode(node);
      });
      node.on('dragend transformend', () => pushHistory());
    };

    // ── Add Elements ──────────────────────────────────────────────────────────────
    const centerX = DESIGN_W / 2;
    const centerY = DESIGN_H / 2;

    const addText = () => {
      // Use the patch area centre so the text appears inside the patch, not at a raw canvas corner
      const pa = effectivePatchArea();
      const cx = pa ? pa.x + pa.width  / 2 : DESIGN_W / 2;
      const cy = pa ? pa.y + pa.height / 2 : DESIGN_H / 2;

      const node = new K.Text({
        text: 'Your Text',
        x: cx,
        y: cy,
        fontSize: Math.round(DESIGN_H / 12),
        fontFamily: 'Arial',
        fill: '#111111',
        align: 'center',
        draggable: true,
        offsetX: 0,
        offsetY: 0,
      });

      attachTextEvents(node);
      attachNodeEvents(node);
      designLayer.add(node);

      // Draw FIRST so Konva measures the text — then centre it.
      // Without this draw, node.width() returns 0 and the offset has no effect.
      designLayer.draw();
      node.offsetX(node.width()  / 2);
      node.offsetY(node.height() / 2);

      designLayer.draw();
      selectNode(node);
      pushHistory();
      // Don't auto-open the textarea — user double-clicks to edit (standard canvas UX).
      // Auto-opening caused the text to visually jump to the wrong position.
    };

    const addUploadedImage = (file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const maxSide = Math.min(DESIGN_W, DESIGN_H) * 0.4;
          const s = Math.min(maxSide / img.width, maxSide / img.height, 1);
          const w = img.width * s;
          const h = img.height * s;
          const node = new K.Image({
            image: img,
            x: centerX - w / 2,
            y: centerY - h / 2,
            width: w,
            height: h,
            draggable: true
          });
          attachNodeEvents(node);
          designLayer.add(node);
          designLayer.draw();
          selectNode(node);
          pushHistory();
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    };

    const addShape = (def) => {
      const size = Math.min(DESIGN_W, DESIGN_H) * 0.22;
      const solidStyle  = { fill: '#111111', stroke: '',        strokeWidth: 0 };
      const outlineStyle = { fill: '',       stroke: '#111111', strokeWidth: Math.max(2, Math.round(size * 0.05)) };
      const style = def.solid ? solidStyle : { ...outlineStyle };
      if (def.dash) { style.dash = [10, 5]; style.dashEnabled = true; }

      let node;
      switch (def.konva) {
        case 'Rect':
          node = new K.Rect({ x: centerX, y: centerY, draggable: true,
            width: Math.round(size * 1.4), height: size, ...style,
            offsetX: Math.round(size * 0.7), offsetY: Math.round(size / 2) });
          break;
        case 'Circle':
          node = new K.Circle({ x: centerX, y: centerY, draggable: true,
            radius: Math.round(size / 2), ...style });
          break;
        case 'RegularPolygon':
          node = new K.RegularPolygon({ x: centerX, y: centerY, draggable: true,
            sides: def.sides, radius: Math.round(size / 2), ...style });
          break;
        case 'Star':
          node = new K.Star({ x: centerX, y: centerY, draggable: true,
            numPoints: 5, innerRadius: Math.round(size * 0.22), outerRadius: Math.round(size / 2), ...style });
          break;
        case 'Line':
          node = new K.Line({ x: centerX, y: centerY, draggable: true,
            points: [-Math.round(size * 0.7), 0, Math.round(size * 0.7), 0],
            stroke: '#111111', strokeWidth: Math.max(3, Math.round(size * 0.05)),
            lineCap: 'round', hitStrokeWidth: 12 });
          break;
        default: return;
      }

      attachNodeEvents(node);
      designLayer.add(node);
      designLayer.draw();
      selectNode(node);
      pushHistory();
      // close drawer
      shapesDrawer.hidden = true;
      shapesBtn?.classList.remove('pck-btn--active');
    };

    // ── Properties Panel ──────────────────────────────────────────────────────────
    const propsEl = toolbar.querySelector(`#pck-props-${sectionId}`);

    const FONTS = ['Arial', 'Georgia', 'Verdana', 'Courier New', 'Times New Roman', 'Impact', 'Trebuchet MS'];

    const renderPropsPanel = () => {
      if (!propsEl) return;
      propsEl.innerHTML = '';
      if (!selectedNode) return;

      const node = selectedNode;
      const isText  = node.className === 'Text';
      const isShape = ['Rect','Circle','RegularPolygon','Star','Line','Arrow'].includes(node.className);

      if (isText) {
        const bold   = node.fontStyle().includes('bold');
        const italic = node.fontStyle().includes('italic');
        const align  = node.align() || 'left';

        propsEl.innerHTML = `
          <div class="pck-prop-group">
            <select class="pck-select" data-prop="fontFamily" title="Font">
              ${FONTS.map(f => `<option value="${f}"${node.fontFamily() === f ? ' selected' : ''}>${f}</option>`).join('')}
            </select>
            <input type="number" class="pck-number" data-prop="fontSize" value="${node.fontSize()}" min="8" max="200" title="Size">
            <input type="color" class="pck-color" data-prop="fill" value="${node.fill()}" title="Color">
            <button type="button" class="pck-btn pck-btn--sm${bold ? ' pck-btn--active' : ''}" data-prop="bold" title="Bold"><b>B</b></button>
            <button type="button" class="pck-btn pck-btn--sm${italic ? ' pck-btn--active' : ''}" data-prop="italic" title="Italic"><i>I</i></button>
            <span class="pck-separator" aria-hidden="true" style="height:20px;margin:0 2px"></span>
            <button type="button" class="pck-btn pck-btn--sm pck-btn--icon-only${align === 'left' ? ' pck-btn--active' : ''}" data-prop="align-left" title="Align left">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z"/></svg>
            </button>
            <button type="button" class="pck-btn pck-btn--sm pck-btn--icon-only${align === 'center' ? ' pck-btn--active' : ''}" data-prop="align-center" title="Align center">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M7 15v2h10v-2H7zm-4 6h18v-2H3v2zm0-8h18v-2H3v2zm4-6v2h10V7H7zM3 3v2h18V3H3z"/></svg>
            </button>
            <button type="button" class="pck-btn pck-btn--sm pck-btn--icon-only${align === 'right' ? ' pck-btn--active' : ''}" data-prop="align-right" title="Align right">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M3 21h18v-2H3v2zm6-4h12v-2H9v2zm-6-4h18v-2H3v2zm6-4h12V7H9v2zM3 3v2h18V3H3z"/></svg>
            </button>
          </div>
        `;

        propsEl.querySelector('[data-prop="fontFamily"]').addEventListener('change', (e) => {
          node.fontFamily(e.target.value); designLayer.draw(); pushHistory();
        });
        propsEl.querySelector('[data-prop="fontSize"]').addEventListener('input', (e) => {
          const v = Math.max(8, Math.min(200, parseInt(e.target.value, 10) || 24));
          node.fontSize(v); node.offsetX(node.width() / 2); node.offsetY(node.height() / 2);
          designLayer.draw(); pushHistory();
        });
        propsEl.querySelector('[data-prop="fill"]').addEventListener('input', (e) => {
          node.fill(e.target.value); designLayer.draw(); pushHistory();
        });
        propsEl.querySelector('[data-prop="bold"]').addEventListener('click', () => {
          const s = node.fontStyle() || '';
          node.fontStyle(s.includes('bold') ? s.replace(/bold\s?/,'').trim() : (s + ' bold').trim());
          designLayer.draw(); pushHistory(); renderPropsPanel();
        });
        propsEl.querySelector('[data-prop="italic"]').addEventListener('click', () => {
          const s = node.fontStyle() || '';
          node.fontStyle(s.includes('italic') ? s.replace(/italic\s?/,'').trim() : (s + ' italic').trim());
          designLayer.draw(); pushHistory(); renderPropsPanel();
        });
        ['left','center','right'].forEach((a) => {
          propsEl.querySelector(`[data-prop="align-${a}"]`)?.addEventListener('click', () => {
            node.align(a); designLayer.draw(); pushHistory(); renderPropsPanel();
          });
        });

      } else if (isShape) {
        const isLine = node.className === 'Line' || node.className === 'Arrow';
        const hasFill = !isLine && node.fill() !== '';
        const currentFill   = node.fill()   || '#111111';
        const currentStroke = node.stroke() || '#111111';
        const currentSW     = node.strokeWidth() || 2;
        propsEl.innerHTML = `
          <div class="pck-prop-group">
            ${!isLine ? `<label class="pck-label">Fill</label>
            <input type="color" class="pck-color" data-prop="fill" value="${currentFill}" title="Fill color" ${!hasFill ? 'style="opacity:.4"' : ''}>
            <label class="pck-label" style="margin-left:4px">Stroke</label>` : `<label class="pck-label">Color</label>`}
            <input type="color" class="pck-color" data-prop="stroke" value="${currentStroke}" title="Stroke color">
            <label class="pck-label" style="margin-left:4px">Width</label>
            <input type="number" class="pck-number" data-prop="strokeWidth" value="${currentSW}" min="0" max="40" title="Stroke width" style="width:46px">
          </div>
        `;
        if (!isLine) {
          propsEl.querySelector('[data-prop="fill"]').addEventListener('input', (e) => {
            node.fill(e.target.value); designLayer.draw(); pushHistory();
          });
        }
        propsEl.querySelector('[data-prop="stroke"]').addEventListener('input', (e) => {
          node.stroke(e.target.value); designLayer.draw(); pushHistory();
        });
        propsEl.querySelector('[data-prop="strokeWidth"]').addEventListener('input', (e) => {
          node.strokeWidth(Math.max(0, parseInt(e.target.value, 10) || 0)); designLayer.draw(); pushHistory();
        });
      }
    };

    // ── Patch position controls ───────────────────────────────────────────────────
    const PATCH_STEP = 10; // pixels per arrow click
    optionsPanel.addEventListener('click', (e) => {
      const moveBtn = e.target.closest('[data-move]');
      if (moveBtn) {
        const dir = moveBtn.dataset.move;
        if (dir === 'up')       patchOffsetY -= PATCH_STEP;
        if (dir === 'down')     patchOffsetY += PATCH_STEP;
        if (dir === 'left')     patchOffsetX -= PATCH_STEP;
        if (dir === 'right')    patchOffsetX += PATCH_STEP;
        if (dir === 'zoom-in')  patchScale = Math.min(3.0, +(patchScale + 0.1).toFixed(2));
        if (dir === 'zoom-out') patchScale = Math.max(0.2, +(patchScale - 0.1).toFixed(2));
        if (dir === 'center')   { patchOffsetX = 0; patchOffsetY = 0; patchScale = 1.0; }
        applyPatchShape(selectedShape?.value);
        return;
      }
    });

    // ── Patch options panel event handling ───────────────────────────────────────
    optionsPanel.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-group]');
      if (!btn) return;
      const group = btn.dataset.group;
      const value = btn.dataset.value;

      // Update active button styling (color group uses its own class)
      if (group === 'color') {
        optionsPanel.querySelectorAll('[data-group="color"]').forEach(b =>
          b.classList.toggle('pck-color-btn--active', b === btn)
        );
        selectedColor = leatheretteColors.find(c => c.value === value) || null;
        applyPatchShape(selectedShape?.value);
        return;
      }

      optionsPanel.querySelectorAll(`[data-group="${group}"]`).forEach(b =>
        b.classList.toggle('pck-patch-btn--active', b === btn)
      );

      if (group === 'decoration') {
        selectedDecoration = decorationMethods.find(m => m.value === value) || null;
        // Show/hide leatherette color picker based on whether this decoration needs a color
        const colorSection = optionsPanel.querySelector('.pck-color-section');
        if (colorSection) {
          const needsColor = !!selectedDecoration?.hasColor;
          colorSection.hidden = !needsColor;
          if (needsColor && !selectedColor) {
            selectedColor = leatheretteColors[0] || null;
            // Mark first color active
            colorSection.querySelector('[data-group="color"]')?.classList.add('pck-color-btn--active');
          }
          if (!needsColor) selectedColor = null;
          applyPatchShape(selectedShape?.value);
        }
      } else if (group === 'shape') {
        selectedShape = patchShapes.find(s => s.value === value) || null;
        applyPatchShape(value);
      } else if (group === 'size') {
        selectedSize = patchSizes.find(s => s.value === value) || null;
        patchArea = computePatchArea(selectedSize?.value);
        patchOffsetX = 0; patchOffsetY = 0; patchScale = 1.0; // re-center on size change
        applyPatchShape(selectedShape?.value);
      }
    });

    // ── Toolbar event handling ────────────────────────────────────────────────────
    toolbar.addEventListener('click', (e) => {
      const action = e.target.closest('[data-action]')?.dataset?.action;
      if (!action) return;
      if (action === 'add-text') addText();
      else if (action === 'upload-image') toolbar.querySelector('[data-upload-input]')?.click();
      else if (action === 'toggle-shapes') {
        const open = shapesDrawer.hidden;
        shapesDrawer.hidden = !open;
        shapesBtn?.classList.toggle('pck-btn--active', open);
      }
      else if (action === 'undo') undo();
      else if (action === 'redo') redo();
      else if (action === 'delete') {
        if (selectedNode) {
          selectedNode.destroy();
          selectedNode = null;
          tr.nodes([]);
          designLayer.draw();
          pushHistory();
          renderPropsPanel();
        }
      }
    });

    // ── Shapes drawer event handling ─────────────────────────────────────────────
    shapesDrawer.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('[data-action="close-shapes"]');
      if (closeBtn) {
        shapesDrawer.hidden = true;
        shapesBtn?.classList.remove('pck-btn--active');
        return;
      }
      const item = e.target.closest('[data-shape]');
      if (!item) return;
      const def = SHAPE_DEFS.find(d => d.id === item.dataset.shape);
      if (def) addShape(def);
    });

    toolbar.querySelector('[data-upload-input]')?.addEventListener('change', (e) => {
      const file = e.target.files?.[0];
      if (file) addUploadedImage(file);
      e.target.value = '';
    });

    // ── Keyboard shortcuts ────────────────────────────────────────────────────────
    const onKeydown = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key === 'z') { e.preventDefault(); undo(); return; }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z'))) { e.preventDefault(); redo(); return; }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedNode) {
        e.preventDefault();
        selectedNode.destroy(); selectedNode = null; tr.nodes([]); designLayer.draw();
        pushHistory(); renderPropsPanel();
      }
    };
    document.addEventListener('keydown', onKeydown);

    // ── Save Design ───────────────────────────────────────────────────────────────
    const saveDesign = async (btn) => {
      if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
      try {
        selectNode(null);
        tr.hide();
        designLayer.draw();

        // Export canvas — may fail with SecurityError if image CORS tainted the canvas
        let dataUrl;
        try {
          dataUrl = await stage.toDataURL({ pixelRatio: 2, mimeType: 'image/png' });
        } catch (corsErr) {
          console.warn('[PCD] stage.toDataURL failed (likely CORS taint), falling back to design-layer export', corsErr);
          try {
            dataUrl = await designLayer.toDataURL({ pixelRatio: 2, mimeType: 'image/png' });
          } catch (e2) {
            console.error('[PCD] Design layer export also failed:', e2);
            dataUrl = '';
          }
        }
        const designJson = snapshotDesign();

        tr.show();
        designLayer.draw();

        const placement     = detail.getCurrentPlacement?.()     || 'front';
        const placementName = detail.getCurrentPlacementName?.() || 'Front';
        const designs       = detail.getPlacementDesigns?.();

        // Persist design snapshot + data URL + current patch options in memory
        designs?.set(placement, {
          snapshot         : designJson,
          previewUrl       : dataUrl,
          placementName,
          decorationMethod : selectedDecoration?.label || '',
          patchShape       : selectedShape?.label      || '',
          patchSize        : selectedSize?.label       || '',
          leatheretteColor : selectedColor?.label      || '',
        });

        // Update thumbnail immediately (data URL is fine for <img> src)
        const pcdWrapper = container.closest('.product-customizer')
          || document.querySelector(`.product-customizer[data-section-id="${sectionId}"]`);
        const thumbImg = pcdWrapper?.querySelector(`[data-pcd-thumb="${placement}"]`);
        if (thumbImg && dataUrl) {
          thumbImg.src = dataUrl;
          thumbImg.hidden = false;
        }

        // Update the preview <img> element directly — do NOT call bridge.setDesign
        // here because that writes the data URL into a hidden form input which
        // gets submitted to Shopify's cart API and triggers a 422 "Cart is too large".
        if (placement === 'front') {
          const inst = bridge._instances.get(sectionId);
          if (inst?.previewEl && dataUrl) {
            inst.previewEl.src = dataUrl;
            inst.previewEl.hidden = false;
          }
        }

        // ── Persist designs to localStorage NOW (at save time, not at submit time) ──
        // Dawn uses AJAX for Add to Cart and reads FormData before our submit listener
        // fires, so we must add the token input to the form here while we still can.
        if (detail.saveMode === 'backend' && detail.uploadEndpoint && detail.productFormId) {
          const form = document.getElementById(detail.productFormId);
          if (form) {
            // Reuse existing token so multiple saves don't orphan localStorage entries
            let tokenInput = form.querySelector('[name="properties[_design_token]"]');
            const token = tokenInput?.value || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

            // Serialise ALL placements saved so far
            const lsPayload = {};
            designs?.forEach((d, pl) => {
              lsPayload[pl] = {
                previewUrl   : d.previewUrl,
                placementName: d.placementName || (pl.charAt(0).toUpperCase() + pl.slice(1)),
                productId    : detail.productId || '',
              };
            });
            // Design payload (2–5 MB) → IndexedDB (no size limit)
            pcdIdbSet(`pcd_designs_${token}`, lsPayload)
              .catch(err => console.warn('[PCD] IDB write failed:', err));
            // Endpoint URL (~100 chars) → localStorage (synchronous, fine)
            try { localStorage.setItem('pcd_upload_endpoint', detail.uploadEndpoint); } catch(_) {}

            // Add (or update) token input so Dawn's AJAX FormData includes it
            if (!tokenInput) {
              tokenInput = document.createElement('input');
              tokenInput.type = 'hidden';
              tokenInput.name = 'properties[_design_token]';
              form.appendChild(tokenInput);
            }
            tokenInput.value = token;

            // Write patch option selections as visible cart line item properties,
            // prefixed with the placement name so multiple placements are distinct.
            // Also removes any legacy non-prefixed inputs from earlier saves.
            const setPropInput = (name, value) => {
              // Note: brackets inside quoted attribute selectors don't need CSS.escape
              let inp = form.querySelector(`[name="${name}"]`);
              if (value) {
                if (!inp) {
                  inp = document.createElement('input');
                  inp.type = 'hidden';
                  inp.name = name;
                  form.appendChild(inp);
                }
                inp.value = value;
              } else if (inp) {
                inp.remove(); // remove if value is now empty
              }
            };

            // Remove legacy non-prefixed inputs (from before this change)
            ['Decoration Method', 'Patch Shape', 'Patch Size'].forEach(k => {
              const old = form.querySelector(`[name="properties[${k}]"]`);
              if (old) old.remove();
            });

            // Write all placements that have been saved in this session
            designs?.forEach((d, _pl) => {
              const prefix = d.placementName || 'Front';
              setPropInput(`properties[${prefix} - Decoration Method]`,  d.decorationMethod);
              setPropInput(`properties[${prefix} - Leatherette Color]`,  d.leatheretteColor);
              setPropInput(`properties[${prefix} - Patch Shape]`,        d.patchShape);
              setPropInput(`properties[${prefix} - Patch Size]`,         d.patchSize);
            });
          }
        }

        bridge.register(sectionId, { hasDesign: true });

        // Mark Customize button as done for this placement
        const pcdBtn = pcdWrapper?.querySelector(`[data-pcd-placement="${placement}"][data-customizer-open]`);
        pcdBtn?.classList.add('pcd-customize-btn--done');

        // Close modal
        const modal = container.closest('[data-customizer-modal]');
        if (modal) {
          modal.hidden = true;
          document.body.classList.remove('product-customizer--open');
        }
      } finally {
        if (btn) { btn.disabled = false; btn.textContent = 'Save Design'; }
      }
    };

    // Wire up the Save button already in the modal header (from liquid)
    const existingSaveBtn = container.closest('.product-customizer__modal-panel')
      ?.querySelector('[data-customizer-save]');
    if (existingSaveBtn) {
      existingSaveBtn.addEventListener('click', () => saveDesign(existingSaveBtn));
    }

    // Cleanup on modal close
    const modal = container.closest('[data-customizer-modal]');
    if (modal) {
      const obs = new MutationObserver(() => {
        if (modal.hidden) {
          document.removeEventListener('keydown', onKeydown);
          window.removeEventListener('resize', sizeEditorAndRescale);
          obs.disconnect();
        }
      });
      obs.observe(modal, { attributes: true, attributeFilter: ['hidden'] });
    }

    // Support variant image update, rescale, and per-placement design restore
    bridge.register(sectionId, {
      konvaStage: stage,
      hasDesign: false,
      updateBaseImage: (url) => loadBg(url || baseImageUrl || ''),
      konvaRescale: sizeEditorAndRescale,
      loadPlacementDesign: (placement, passedImageUrl) => {
        const designs  = detail.getPlacementDesigns?.();
        const saved    = designs?.get(placement);
        // Prefer the URL passed directly from the open handler (already computed from the clicked
        // button's data-pcd-image). Fall back to button lookup, then to the initial baseImageUrl.
        let imgUrl = passedImageUrl;
        if (!imgUrl) {
          const pcdWrapper = container.closest('.product-customizer');
          const btn = pcdWrapper?.querySelector(`[data-pcd-placement="${placement}"][data-customizer-open]`);
          imgUrl = btn?.dataset?.pcdImage || baseImageUrl || '';
        }
        loadBg(imgUrl);
        // Restore saved design nodes or clear canvas
        designLayer.getChildren(n => n !== tr).forEach(n => n.destroy());
        if (saved?.snapshot) {
          try {
            const nodes = JSON.parse(saved.snapshot);
            nodes.forEach((cfg) => {
              const KClass = window.Konva[cfg.className];
              if (!KClass) return;
              const node = new KClass(cfg.attrs);
              if (cfg.className === 'Text') attachTextEvents(node);
              attachNodeEvents(node);
              designLayer.add(node);
            });
          } catch(e) { /* ignore parse error */ }
        }
        tr.nodes([]);
        designLayer.draw();
      }
    });
  };

  const initImglyCustomizer = async (detail) => {
    const {
      sectionId,
      editorConfig,
      uploadEndpoint,
      container,
      productFormId,
      saveMode,
      baseImageUrl,
      getVariantImageUrl,
      onSdkReady
    } = detail;
    if (!container) return;

    const sdk = window.CreativeEditorSDK || window.CESDK;
    if (!sdk?.create) {
      console.warn('[ProductCustomizer] CreativeEditorSDK not available.');
      const target =
        container.closest('.product-customizer')?.querySelector('[data-customizer-error]') ||
        container.closest('.product-customizer__modal')?.querySelector('[data-customizer-error]');
      if (target) {
        target.hidden = false;
        target.textContent =
          'Editor failed to load. Please refresh the page or check the console for errors.';
      }
      return;
    }


    // Ensure container has dimensions before SDK init
    const containerParent = container.parentElement;

    // Force explicit dimensions if container has 0 size
    if (container.offsetWidth === 0 || container.offsetHeight === 0) {

      // Get parent dimensions and set absolute sizes
      const parentWidth = containerParent?.offsetWidth || window.innerWidth;
      const parentHeight = containerParent?.offsetHeight || window.innerHeight;

      // Account for padding and header (roughly 100px)
      const editorWidth = Math.max(parentWidth - 40, 800); // min 800px
      const editorHeight = Math.max(parentHeight - 100, 600); // min 600px


      container.style.width = `${editorWidth}px`;
      container.style.height = `${editorHeight}px`;
      container.style.minWidth = '800px';
      container.style.minHeight = '600px';
      container.style.display = 'block';
      container.style.overflow = 'hidden';

      // Force immediate reflow by reading layout properties
      void container.offsetWidth;
      void containerParent.offsetHeight;

      // Wait for browser to recalculate layout
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Clear container - SDK will mount its own content
    container.innerHTML = '';
    container.classList.add('product-customizer__canvas');

    const config = {
      ...editorConfig,
      role: 'Creator',  // Use Creator role for full UI access
    };
    if (!config.license) {
      config.license = DEFAULT_IMGLY_LICENSE;
    }
    if (!config.baseURL) {
      config.baseURL = `https://cdn.img.ly/packages/imgly/cesdk-js/${DEFAULT_IMGLY_VERSION}/assets`;
    }

    // Use default UI configuration - let SDK show all default panels
    // Don't override ui.elements to allow full editor interface


    let cesdk;
    try {
      cesdk = await sdk.create(container, config);

      // Force resize calculation after SDK mounts
      window.dispatchEvent(new Event('resize'));

      // Check dimensions again after SDK is created

      if (config.loadDefaultAssetSources !== false) {

        // Add demo asset sources (needed for dock items to exist)
        await cesdk.addDefaultAssetSources();
        await cesdk.addDemoAssetSources({
          sceneMode: 'Design',
          withUploadAssetSources: true
        });

        // Remove unwanted dock items - keep only Uploads, Text, and Shapes

        // List of dock items to remove (keep only: ly.img.upload, ly.img.text, ly.img.vectorpath)
        const itemsToRemove = [
          'ly.img.template',
          'ly.img.video.template',
          'ly.img.elements',  // Note: plural!
          'ly.img.image',
          'ly.img.video',
          'ly.img.audio',
          'ly.img.sticker'
        ];

        itemsToRemove.forEach(key => {
          try {
            cesdk.ui.removeDockOrderComponent({ key });
          } catch (e) {
          }
        });

        // Also remove separator if present
        try {
          cesdk.ui.removeDockOrderComponent({ id: 'ly.img.separator' });
        } catch (e) { /* separator might not exist */ }

        // Log remaining dock items
        const dockOrder = cesdk.ui.getDockOrder();
      }

      const applyBaseImage = async (imageUrl) => {
        const resolvedUrl = imageUrl || getVariantImageUrl?.();

        if (resolvedUrl) {
          await cesdk.createFromImage(resolvedUrl);
        } else {
          await cesdk.createDesignScene();
        }
      };

      await applyBaseImage(baseImageUrl);

      // Disable UI features and hide page label AFTER scene is created
      try {

        cesdk.feature.disable('ly.img.preview');
        cesdk.feature.disable('ly.img.page.resize');

        cesdk.feature.disable('ly.img.inspector.bar');

        cesdk.feature.disable('ly.img.replace');
        cesdk.feature.disable('ly.img.placeholder');

        // Hide page title using the official engine settings API
        try {
          cesdk.engine.editor.setSettingBool('page/title/show', false);
        } catch (e) {
        }

      } catch (error) {
        console.error('[ProductCustomizer] Error disabling features:', error);
      }

      // CRITICAL FIX: Force SDK root element to be visible
      const rootShadow = container.querySelector('#root-shadow, [data-ubq-theme]');
      if (rootShadow) {
        rootShadow.style.display = 'block';
        rootShadow.style.width = '100%';
        rootShadow.style.height = '100%';
        rootShadow.style.position = 'relative';

        // Force reflow
        void rootShadow.offsetHeight;
      } else {
        console.warn('[ProductCustomizer] SDK root element NOT found!');
      }

      // Call onSdkReady callback to store SDK instance
      if (onSdkReady && typeof onSdkReady === 'function') {
        onSdkReady(cesdk);
      }
    } catch (error) {
      console.warn('[ProductCustomizer] Failed to initialize CreativeEditorSDK.', error);
      const fallback =
        container.closest('.product-customizer')?.querySelector('[data-customizer-error]') ||
        container.closest('.product-customizer__modal')?.querySelector('[data-customizer-error]');
      if (fallback) {
        fallback.hidden = false;
        fallback.textContent =
          'Unable to load the editor or base image. Check the browser console for more details.';
      }
      return;
    }

    const ensureSaveButton = () => {
      const header = container.closest('.product-customizer__modal-panel')?.querySelector(
        '.product-customizer__modal-header'
      );
      if (!header || header.querySelector('[data-customizer-save]')) return null;
      const saveButton = document.createElement('button');
      saveButton.type = 'button';
      saveButton.className = 'product-customizer__save';
      saveButton.textContent = 'Save design';
      saveButton.setAttribute('data-customizer-save', '');
      header.appendChild(saveButton);
      return saveButton;
    };

    const saveDesign = async () => {
      const designJson = await cesdk.save();
      const exportResult = await cesdk.export({ mimeType: 'image/png' });
      const previewBlob = exportResult?.blobs?.[0];
      const previewDataUrl = previewBlob ? await blobToDataUrl(previewBlob) : '';

      const variantInput = productFormId
        ? document.querySelector(`#${productFormId} .product-variant-id`)
        : null;
      const variantId = variantInput?.value || '';

      if (saveMode === 'backend' && uploadEndpoint && previewBlob) {
        try {
          const formData = new FormData();
          formData.append('design_json', designJson);
          formData.append('variant_id', variantId);
          formData.append('preview', previewBlob, 'design.png');

          const response = await fetch(uploadEndpoint, {
            method: 'POST',
            body: formData
          });
          const payload = await response.json();

          bridge.setDesign(sectionId, {
            designJson,
            previewUrl: payload?.previewUrl || previewDataUrl,
            designId: payload?.designId || ''
          });
        } catch (error) {
          console.warn('[ProductCustomizer] Upload failed, using data URL.', error);
          bridge.setDesign(sectionId, { designJson, previewUrl: previewDataUrl, designId: '' });
        }
      } else {
        bridge.setDesign(sectionId, { designJson, previewUrl: previewDataUrl, designId: '' });
      }

      const instance = bridge._instances.get(sectionId);
      if (instance) {
        instance.hasDesign = true;
      }
    };

    const saveButton = ensureSaveButton();
    if (saveButton) {
      saveButton.addEventListener('click', saveDesign);
    }

    bridge.register(sectionId, {
      cesdk,
      hasDesign: false,
      updateBaseImage: (url) => applyBaseImage(url || getVariantImageUrl?.())
    });
  };
})();
